// 자녀 관리 페이지 (Supabase 직접 조회)

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';

// 자녀 타입
interface Child {
  id: string;
  name: string;
  organizationName: string;
  subscriptionStatus: 'active' | 'expired' | 'none';
  subscriptionEndDate?: string;
  relationship?: string;
}

export default function ChildrenPage() {
  const navigate = useNavigate();
  const { parent } = useSupabaseParentAuth();

  const [children, setChildren] = useState<Child[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (parent?.id) {
      loadChildren();
    }
  }, [parent?.id]);

  const loadChildren = async () => {
    setIsLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch('/api/v1/parent/children', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '자녀 목록 조회 실패');
      }

      const data = await response.json();

      // 자녀 데이터 변환
      const childrenData: Child[] = data.map((child: any) => ({
        id: child.id,
        name: child.name || '이름 없음',
        organizationName: child.organization_name || '독서실',
        subscriptionStatus: 'active' as const, // TODO: 실제 이용권 상태 조회
        relationship: child.relationship,
      }));

      setChildren(childrenData);
    } catch (err: any) {
      console.error('데이터 로드 오류:', err);
      setError(err.message || '자녀 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysRemaining = (endDate?: string) => {
    if (!endDate) return null;

    const today = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    return diff;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        자녀 관리
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        등록된 자녀 목록과 이용권 상태를 확인할 수 있습니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {children.length === 0 ? (
        <Alert severity="info">
          등록된 자녀가 없습니다. 독서실에 문의하여 자녀를 연결해주세요.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>이름</TableCell>
                <TableCell>독서실</TableCell>
                <TableCell>관계</TableCell>
                <TableCell>이용권 상태</TableCell>
                <TableCell align="right">관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {children.map((child) => {
                const daysRemaining = getDaysRemaining(child.subscriptionEndDate);

                return (
                  <TableRow key={child.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {child.name}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {child.organizationName}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {child.relationship || '부모'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {child.subscriptionStatus === 'active' ? (
                        <Chip label="이용 중" color="success" size="small" />
                      ) : child.subscriptionStatus === 'expired' ? (
                        <Chip label="만료" color="error" size="small" />
                      ) : (
                        <Chip label="정보 없음" color="default" size="small" />
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => navigate(`/parent/attendance/${child.id}`)}
                        >
                          출석 보기
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CalendarMonthIcon />}
                          onClick={() => navigate(`/parent/period-schedule/${child.id}`)}
                        >
                          교시 스케줄
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
